import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionManager } from '../../utils/SessionManager';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { sendAdminNotification } from '../../services/notificationService';
import { SnackbarType } from '../../components/Snackbar';

import {
  Container,
  Title,
  FormGroup,
  Label,
  TextArea,
  Button,
  AlertBox,
  CharCounter,
  AlertMessage
} from './AdminNotifications.styles';

import {
  AdminNotificationsProps,
  AlertType,
  AlertData,
  AdminNotificationResult,
  User
} from './AdminNotifications.types';

// 定数
const MAX_LENGTH = 300;
const REDIRECT_DELAY = 2000;

/**
 * 管理者向け通知送信ページコンポーネント
 */
const AdminNotifications: React.FC<AdminNotificationsProps> = ({
  sessionManager = SessionManager,
  notificationService = sendAdminNotification,
  navigate: customNavigate
}) => {
  const [message, setMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertData | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  
  const defaultNavigate = useNavigate();
  const navigate = customNavigate || defaultNavigate;
  const showSnackbar = useSnackbar();

  /**
   * 管理者権限チェック
   */
  const checkAdminAuth = async (): Promise<void> => {
    try {
      const user = sessionManager.getUser();
      if (!user || !('role' in user) || user.role !== 'admin') {
        setAlert({
          type: 'error',
          message: '管理者権限が必要です。'
        });
        setIsAdmin(false);
        // リダイレクトは少し遅らせる
        setTimeout(() => navigate('/'), REDIRECT_DELAY);
      } else {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error('認証エラー:', error);
      setIsAdmin(false);
      setAlert({
        type: 'error',
        message: '認証に失敗しました。再ログインしてください。'
      });
    }
  };

  /**
   * 通知送信処理
   */
  const handleSendNotification = async (): Promise<void> => {
    // バリデーション
    if (message.trim() === '') {
      setAlert({
        type: 'error',
        message: 'メッセージを入力してください。'
      });
      return;
    }

    if (message.length > MAX_LENGTH) {
      setAlert({
        type: 'error',
        message: `メッセージは${MAX_LENGTH}文字以内で入力してください。`
      });
      return;
    }

    setIsSending(true);
    setAlert(null);

    try {
      // notificationServiceを使用して通知を送信
      const result: AdminNotificationResult = await notificationService(message.trim());

      if (result.success) {
        setAlert({
          type: 'success',
          message: `通知が正常に送信されました。送信先: ${result.data?.sentCount || '全'}ユーザー`
        });
        // スナックバーでも成功メッセージを表示
        showSnackbar('通知が正常に送信されました', SnackbarType.SUCCESS);
        setMessage('');
      } else {
        throw new Error(result.message || '通知の送信に失敗しました。');
      }
    } catch (error: any) {
      console.error('通知送信エラー:', error);
      const errorMessage = error.response?.data?.message || error.message || '通知の送信中にエラーが発生しました。';
      
      setAlert({
        type: 'error',
        message: errorMessage
      });
      // スナックバーでもエラーメッセージを表示
      showSnackbar('通知の送信に失敗しました', SnackbarType.ERROR);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * メッセージ変更ハンドラー
   */
  const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setMessage(event.target.value);
  };

  // 管理者権限チェック
  useEffect(() => {
    checkAdminAuth();
  }, []);

  // 管理者権限がない場合の表示
  if (!isAdmin) {
    return (
      <Container>
        <AlertMessage alert={alert} />
        <p>権限を確認中...</p>
      </Container>
    );
  }

  // 送信ボタンの無効化判定
  const isButtonDisabled = isSending || message.trim() === '' || message.length > MAX_LENGTH;
  
  // 文字数制限超過の判定
  const isCharCountOver = message.length > MAX_LENGTH;

  return (
    <Container data-testid="admin-notifications-container">
      <Title>管理者通知送信</Title>
      
      <AlertMessage alert={alert} />
      
      <FormGroup>
        <Label htmlFor="notification-message">全ユーザーへのメッセージ</Label>
        <TextArea
          id="notification-message"
          data-testid="notification-message"
          value={message}
          onChange={handleMessageChange}
          placeholder="こちらに全ユーザーに送信したいメッセージを入力してください"
          disabled={isSending}
        />
        <CharCounter $isOver={isCharCountOver} data-testid="char-counter">
          {message.length}/{MAX_LENGTH}文字
          {isCharCountOver && ' (文字数超過)'}
        </CharCounter>
      </FormGroup>
      
      <Button 
        data-testid="send-button"
        onClick={handleSendNotification}
        disabled={isButtonDisabled}
      >
        {isSending ? '送信中...' : '全ユーザーに通知を送信'}
      </Button>
      
      <div style={{ marginTop: 20 }}>
        <p>
          ※ この機能では、全てのユーザーに一斉に通知を送信します。<br />
          ※ メンテナンス情報や重要なお知らせなど、必要な場合にのみ使用してください。
        </p>
      </div>
    </Container>
  );
};

export default AdminNotifications; 